import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAccessControl from "../context/useAccessControl";
import { getRestaurantProfile } from "../../services/local/restaurantProfile";
import { isDeliveryOnly } from "../../services/local/merchantExperience";
import {
  OPERATIONAL_WORKSPACE_BADGES_UPDATED_EVENT,
  readOperationalWorkspaceBadgeCounts,
} from "../../services/local/serviceNotifications";

const WORKSPACES = {
  salon: {
    number: "01",
    title: "Salón",
    detail: "Mesas y servicio",
    description: "Mesas, llamados y pedidos realizados dentro del salón.",
    route: "/app",
    moduleKey: "inicio",
  },
  delivery: {
    number: "02",
    title: "Delivery",
    detail: "Pedidos externos",
    description: "Pedidos de delivery, retiro, clientes y canales externos.",
    route: "/app/pedidos",
    moduleKey: "pedidos",
  },
};

const formatBadgeCount = (count) => (count > 99 ? "99+" : String(count));

export default function OperationalWorkspaceSwitcher({ activeWorkspace = "salon" }) {
  const navigate = useNavigate();
  const { activeBranchScope, canAccessModule, currentBranch, isKioskBranch } =
    useAccessControl();
  const branchId = currentBranch?.id || activeBranchScope?.id || "";
  const [notificationCounts, setNotificationCounts] = useState(() =>
    readOperationalWorkspaceBadgeCounts(branchId)
  );
  const active = WORKSPACES[activeWorkspace] || WORKSPACES.salon;

  useEffect(() => {
    setNotificationCounts(readOperationalWorkspaceBadgeCounts(branchId));

    const handleBadgeUpdate = (event) => {
      const eventBranchId = String(event?.detail?.branchId || "default").trim();
      const currentBranchId = String(branchId || "default").trim();

      if (eventBranchId !== currentBranchId) {
        return;
      }

      setNotificationCounts(
        event.detail?.counts || readOperationalWorkspaceBadgeCounts(branchId)
      );
    };

    window.addEventListener(
      OPERATIONAL_WORKSPACE_BADGES_UPDATED_EVENT,
      handleBadgeUpdate
    );

    return () => {
      window.removeEventListener(
        OPERATIONAL_WORKSPACE_BADGES_UPDATED_EVENT,
        handleBadgeUpdate
      );
    };
  }, [branchId]);

  if (isKioskBranch || isDeliveryOnly(getRestaurantProfile(currentBranch || activeBranchScope))) {
    return null;
  }

  return (
    <section
      className="operational-workspace-switcher"
      style={styles.switcher}
      aria-label="Cambiar sector operativo"
    >
      <style>
        {`
          @media (max-width: 820px) {
            .operational-workspace-switcher {
              grid-template-columns: minmax(0, 1fr) !important;
              background: linear-gradient(180deg, #0f172a 0%, #1e293b 42%, #fff7ed 42%, #ffffff 100%) !important;
            }
          }

          @media (max-width: 520px) {
            .operational-workspace-tabs {
              grid-template-columns: minmax(0, 1fr) !important;
            }
          }
        `}
      </style>

      <div style={styles.copy}>
        <span style={styles.eyebrow}>Centro operativo</span>
        <strong style={styles.title}>{active.title}</strong>
        <span style={styles.description}>{active.description}</span>
      </div>

      <div
        className="operational-workspace-tabs"
        style={styles.tabs}
        role="tablist"
        aria-label="Sectores de trabajo"
      >
        {Object.entries(WORKSPACES).map(([workspaceKey, workspace]) => {
          const isActive = workspaceKey === activeWorkspace;
          const isEnabled = canAccessModule(workspace.moduleKey);
          const badgeCount = Math.max(
            0,
            Number(notificationCounts?.[workspaceKey] || 0)
          );

          return (
            <button
              key={workspaceKey}
              type="button"
              role="tab"
              aria-selected={isActive}
              style={{
                ...styles.tab,
                ...(isActive ? styles.tabActive : {}),
                ...(!isEnabled ? styles.tabDisabled : {}),
              }}
              disabled={!isEnabled}
              onClick={() => navigate(workspace.route)}
            >
              {isEnabled && badgeCount > 0 ? (
                <span
                  style={styles.notificationBadge}
                  aria-label={`${badgeCount} pedidos sin atender`}
                >
                  {formatBadgeCount(badgeCount)}
                </span>
              ) : null}
              <span style={styles.tabNumber}>{workspace.number}</span>
              <span>
                <strong style={styles.tabTitle}>{workspace.title}</strong>
                <small style={styles.tabDetail}>{workspace.detail}</small>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const styles = {
  switcher: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 0.8fr) minmax(420px, 1.2fr)",
    gap: "18px",
    alignItems: "stretch",
    padding: "18px",
    borderRadius: "24px",
    border: "1px solid rgba(249, 115, 22, 0.28)",
    background:
      "linear-gradient(120deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.97) 43%, rgba(255, 247, 237, 0.98) 43%, rgba(255, 255, 255, 0.98) 100%)",
    boxShadow: "0 20px 45px rgba(15, 23, 42, 0.1)",
  },
  copy: {
    display: "grid",
    alignContent: "center",
    gap: "6px",
    padding: "4px 10px",
    color: "#ffffff",
  },
  eyebrow: {
    color: "#fb923c",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "clamp(28px, 3vw, 42px)",
    lineHeight: 1,
  },
  description: {
    maxWidth: "520px",
    color: "#cbd5e1",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  tabs: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  tab: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: "12px",
    alignItems: "center",
    minHeight: "94px",
    padding: "15px",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    background: "rgba(255, 255, 255, 0.92)",
    color: "#334155",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  },
  notificationBadge: {
    position: "absolute",
    top: "-10px",
    right: "-10px",
    display: "grid",
    placeItems: "center",
    minWidth: "30px",
    height: "30px",
    padding: "0 9px",
    borderRadius: "999px",
    border: "3px solid #ffffff",
    background: "#ef233c",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: 900,
    lineHeight: 1,
    boxShadow: "0 12px 24px rgba(239, 35, 60, 0.32)",
    zIndex: 2,
  },
  tabActive: {
    borderColor: "#f97316",
    background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 100%)",
    color: "#9a3412",
    boxShadow: "0 14px 32px rgba(249, 115, 22, 0.16)",
  },
  tabDisabled: {
    cursor: "not-allowed",
    opacity: 0.52,
  },
  tabNumber: {
    display: "grid",
    placeItems: "center",
    width: "42px",
    height: "42px",
    borderRadius: "14px",
    background: "#0f172a",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
  },
  tabTitle: {
    display: "block",
    fontSize: "19px",
    lineHeight: 1.1,
  },
  tabDetail: {
    display: "block",
    marginTop: "5px",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
  },
};
